// <pfs-login-form>: Pre-Auth-Login-Card mit server-seitigem OAuth-Code-Flow
// via browser-nativem Form-POST (PFS-425 — konsolidiert aus pfs-login nach
// @pfs/components, damit alle Hosts EINE brandbare Login-Form teilen).
//
// Endpoint-agnostisch (data-action) — funktioniert standalone, im Embed und
// als BFF-Login (sparkden/mednet). Token-basiert/brandbar (--pfs-color-*),
// kein adoptStyles; folgt dem @pfs/components-WC-Kanon (defineComponent +
// withShadow). Markup via createElement (Browser quotet Attribute → kein XSS).
//
// Attribute (alle data-*):
//   data-action          required — Form-POST-URL (z.B. "/auth/login").
//   data-return-to       optional — hidden input value (Default "/").
//   data-error-return-to optional — Fehler-Pfad (hidden input error_return_to).
//   data-error-message   optional — gesetzt → pfs:toast variant:"error" beim Mount.
//   data-error-title     optional — Toast-Title (Default "Anmeldung fehlgeschlagen").
//   data-heading         optional — Überschrift (Default "Anmelden"); "" → keine.
//   data-submit-label    optional — Button-Text (Default "Anmelden").
//   data-busy-label      optional — Busy-Text (Default "Wird angemeldet…").
//
// Busy-Kanon (PFS-559): beim Submit wird der Button gesperrt + zeigt Spinner +
// Busy-Label — sichtbares "es passiert was" + Doppel-Submit-Schutz. Da der
// Form-POST die Seite navigiert, bleibt der Busy-State bis zum Page-Wechsel.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { CARD_CSS } from "../../styles/card.js";

function dispatchToast(variant: "error", title: string, message: string): void {
  document.body.dispatchEvent(
    new CustomEvent("pfs:toast", { detail: { title, message, variant }, bubbles: true, composed: true }),
  );
}

function attrOr(el: HTMLElement, name: string, fallback: string): string {
  const v = el.getAttribute(name);
  return v !== null && v !== "" ? v : fallback;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; color: var(--pfs-color-on-surface); }
${CARD_CSS}
.card { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.1rem; }
h1 { margin: 0; font-size: 1.15rem; font-weight: 600; }
form { display: flex; flex-direction: column; gap: 1rem; }
.field { display: flex; flex-direction: column; gap: 0.35rem; }
label { font-size: 0.85rem; font-weight: 500; color: var(--pfs-color-on-surface-muted); }
input {
  font: inherit; padding: 0.55rem 0.75rem; width: 100%; box-sizing: border-box;
  border: 1px solid var(--pfs-color-outline-strong);
  border-radius: var(--pfs-radius-sm, 0.375rem);
  background: var(--pfs-color-surface-sunken); color: var(--pfs-color-on-surface);
}
input::placeholder { color: var(--pfs-color-on-surface-subtle); }
input:focus { outline: none; border-color: var(--pfs-color-accent); box-shadow: var(--pfs-shadow-focus-ring, 0 0 0 3px rgba(59,130,246,0.35)); }
${BUTTON_CSS}
/* Busy-Spinner (Plattform-Kanon). */
.spin { display:inline-block; width:0.9em; height:0.9em; margin-right:0.45em; vertical-align:-0.1em; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation: pfs-spin 0.7s linear infinite; }
@keyframes pfs-spin { to { transform: rotate(360deg); } }
`;

defineComponent({
  tagName: "pfs-login-form",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;

    const action = attrOr(host, "data-action", "/login");
    const returnTo = attrOr(host, "data-return-to", "/");
    const errorReturnTo = attrOr(host, "data-error-return-to", "");
    const heading = host.getAttribute("data-heading");
    const submitLabel = attrOr(host, "data-submit-label", "Anmelden");
    const busyLabel = attrOr(host, "data-busy-label", "Wird angemeldet…");

    const style = document.createElement("style");
    style.textContent = STYLES;

    const card = document.createElement("div");
    card.className = "card";

    // Überschrift: data-heading="" unterdrückt sie (Host hat eigene).
    if (heading === null || heading !== "") {
      const h1 = document.createElement("h1");
      h1.textContent = heading ?? "Anmelden";
      card.appendChild(h1);
    }

    const form = document.createElement("form");
    form.method = "POST";
    form.action = action;

    const hiddenInput = (name: string, value: string): void => {
      const i = document.createElement("input");
      i.type = "hidden";
      i.name = name;
      i.value = value;
      form.appendChild(i);
    };
    hiddenInput("return_to", returnTo);
    if (errorReturnTo) hiddenInput("error_return_to", errorReturnTo);

    const field = (labelText: string, name: string, type: string, autocomplete: string, autofocus = false): void => {
      const wrap = document.createElement("div");
      wrap.className = "field";
      const lbl = document.createElement("label");
      lbl.textContent = labelText;
      const inp = document.createElement("input");
      inp.name = name;
      inp.type = type;
      inp.setAttribute("autocomplete", autocomplete);
      inp.required = true;
      if (autofocus) inp.autofocus = true;
      wrap.append(lbl, inp);
      form.appendChild(wrap);
    };
    field("Benutzername / E-Mail", "username", "text", "username", true);
    field("Passwort", "password", "password", "current-password");

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "btn primary block";
    submit.textContent = submitLabel;
    form.appendChild(submit);

    // Busy-State beim Submit (Kanon): Button sperren + Spinner + Label. Der
    // native POST navigiert die Seite — der Busy-State bleibt bis dahin sichtbar
    // (+ verhindert Doppel-Submit). Bei Fehl-Login lädt /login frisch → reset.
    form.addEventListener("submit", () => {
      submit.disabled = true;
      submit.innerHTML = `<span class="spin" aria-hidden="true"></span>${busyLabel.replace(/[&<>"]/g, "")}`;
    });

    card.appendChild(form);
    root.replaceChildren(style, card);

    // Fehler-Toast (data-error-message) — cross-Shadow via pfs:toast.
    const errMsg = host.getAttribute("data-error-message");
    if (errMsg && errMsg.trim() !== "") {
      const errTitle = attrOr(host, "data-error-title", "Anmeldung fehlgeschlagen");
      const fire = (): void => {
        if (!host.isConnected) return;
        dispatchToast("error", errTitle, errMsg);
      };
      if (customElements.get("pfs-toast-host")) queueMicrotask(fire);
      else void customElements.whenDefined("pfs-toast-host").then(fire);
    }
  },
});
